import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Loader2, ArrowDown } from 'lucide-react';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  disabled?: boolean;
  className?: string;
  pullDownThreshold?: number;
  maxPullDistance?: number;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  disabled = false,
  className,
  pullDownThreshold = 60,
  maxPullDistance = 100,
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Функции обработчиков событий
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (disabled || isRefreshing) return;
    
    // Проверяем, что пользователь находится в верхней части страницы
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 10) return;

    setStartY(e.touches[0].clientY);
    setIsPulling(true);
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling || disabled || isRefreshing) return;

    const currentY = e.touches[0].clientY;
    const pullDistance = Math.max(0, currentY - startY);
    
    // Ограничиваем максимальное расстояние протягивания
    const limitedDistance = Math.min(pullDistance, maxPullDistance);
    
    if (limitedDistance > 0) {
      e.preventDefault(); // Предотвращаем скролл страницы
      setPullDistance(limitedDistance);
    }
  }, [isPulling, disabled, isRefreshing, startY, maxPullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled || isRefreshing) return;

    setIsPulling(false);

    if (pullDistance >= pullDownThreshold) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Ошибка при обновлении:', error);
      } finally {
        setIsRefreshing(false);
      }
    }

    setPullDistance(0);
  }, [isPulling, disabled, isRefreshing, pullDistance, pullDownThreshold, onRefresh]);

  // Обработка mouse событий для тестирования на десктопе
  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || isRefreshing) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 10) return;

    setStartY(e.clientY);
    setIsPulling(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPulling || disabled || isRefreshing) return;

    const currentY = e.clientY;
    const pullDistance = Math.max(0, currentY - startY);
    const limitedDistance = Math.min(pullDistance, maxPullDistance);
    
    if (limitedDistance > 0) {
      e.preventDefault();
      setPullDistance(limitedDistance);
    }
  };

  const handleMouseUp = async () => {
    if (!isPulling || disabled || isRefreshing) return;

    setIsPulling(false);

    if (pullDistance >= pullDownThreshold) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Ошибка при обновлении:', error);
      } finally {
        setIsRefreshing(false);
      }
    }

    setPullDistance(0);
  };

  // Настройка touch событий с non-passive слушателями
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Добавляем touch события с { passive: false } для возможности preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Cleanup mouse events
  useEffect(() => {
    if (isPulling) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const currentY = e.clientY;
        const pullDistance = Math.max(0, currentY - startY);
        const limitedDistance = Math.min(pullDistance, maxPullDistance);
        
        if (limitedDistance > 0) {
          e.preventDefault();
          setPullDistance(limitedDistance);
        }
      };

      const handleGlobalMouseUp = async () => {
        if (!isPulling || disabled || isRefreshing) return;

        setIsPulling(false);

        if (pullDistance >= pullDownThreshold) {
          setIsRefreshing(true);
          try {
            await onRefresh();
          } catch (error) {
            console.error('Ошибка при обновлении:', error);
          } finally {
            setIsRefreshing(false);
          }
        }

        setPullDistance(0);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isPulling, startY, pullDistance, pullDownThreshold, disabled, isRefreshing, onRefresh]);

  const getPullIndicatorStyles = () => {
    const progress = Math.min(pullDistance / pullDownThreshold, 1);
    const opacity = Math.min(pullDistance / 40, 1);
    const height = Math.min(pullDistance, 60);
    
    return {
      transform: `translateY(-${60 - height}px)`, // Показываем индикатор сверху
      opacity,
      height,
    };
  };

  const getIndicatorIcon = () => {
    if (isRefreshing) {
      return <Loader2 className="h-5 w-5 animate-spin text-blue-600" />;
    }
    
    const progress = pullDistance / pullDownThreshold;
    if (progress >= 1) {
      return <ArrowDown className="h-5 w-5 text-green-600 transform rotate-180" />;
    }
    
    return (
      <ArrowDown 
        className="h-5 w-5 text-muted-foreground transition-transform" 
        style={{ transform: `rotate(${progress * 180}deg)` }}
      />
    );
  };

  const getIndicatorText = () => {
    if (isRefreshing) return 'Обновление...';
    if (pullDistance >= pullDownThreshold) return 'Отпустите для обновления';
    if (pullDistance > 0) return 'Потяните для обновления';
    return '';
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Pull Indicator - отображается над контентом */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center bg-blue-50 border-b border-blue-100 transition-all duration-200 ease-out shadow-sm"
        style={getPullIndicatorStyles()}
      >
        <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
          {getIndicatorIcon()}
          <span>{getIndicatorText()}</span>
        </div>
      </div>

      {/* Content - сдвигается вниз при протягивании */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{
          transform: `translateY(${pullDistance}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;